import React from 'react';
import { useUser } from '../../contexts/UserContext';
import { ShopItem } from '../../types';
import ShopItemDetailModal from '../ShopItemDetailModal';

const ShopPage: React.FC = () => {
  const { profile, purchaseItem, shopItems } = useUser();
  const [selectedFilter, setSelectedFilter] = React.useState<string>('all');
  const [selectedItem, setSelectedItem] = React.useState<ShopItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
  
  if (!profile) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  const filteredItems = selectedFilter === 'all' 
    ? shopItems 
    : shopItems.filter(item => item.type === selectedFilter);

  const filters = ['all', 'xp_boost', 'stat_boost', 'special', 'cosmetic'];

  const filterColors: Record<string, string> = {
    all: 'from-system-blue to-system-purple',
    xp_boost: 'from-system-blue to-system-purple',
    stat_boost: 'from-system-blue via-system-purple to-system-blue',
    currency_boost: 'from-system-purple to-system-blue',
    cosmetic: 'from-system-purple to-system-blue',
    special: 'from-system-blue to-system-purple',
  };

  const handleItemClick = (item: ShopItem) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  };

  const handlePurchase = (item: ShopItem) => {
    const success = purchaseItem(item.id);
    if (success) {
      setIsDetailModalOpen(false);
      setSelectedItem(null);
    }
  };

  return (
    <div className="animate-fade-in max-w-7xl mx-auto w-full p-3 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse shadow-neon-blue"></div>
            <span className="font-orbitron text-lg sm:text-2xl tracking-wider text-system-blue uppercase">
              ［ ITEM SHOP ］
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
            Purchase items to enhance your abilities
          </p>
        </div>
      </div>

      {/* Currency Display */}
      <div 
        className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-6 mb-6 hover:border-yellow-500/60 transition-all"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-400 font-rajdhani uppercase tracking-wide mb-2">Your Currency</div>
            <div className="flex items-baseline gap-3">
              <img 
                src="/icons/icon-gold.png" 
                alt="Gold"
                className="w-8 h-8 sm:w-12 sm:h-12 object-contain"
                style={{ filter: 'drop-shadow(0 0 12px rgba(252, 211, 77, 0.8))' }}
              />
              <span className="text-3xl sm:text-5xl font-black font-orbitron" style={{ color: '#FCD34D', textShadow: '0 0 20px rgba(252, 211, 77, 0.8)' }}>
                {profile.currency}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {filters.map((filter) => {
          const isSelected = selectedFilter === filter;
          return (
            <button
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              className={`px-3 sm:px-4 py-2 rounded-2xl font-orbitron font-bold text-xs sm:text-sm tracking-wider transition-all relative overflow-hidden ${
                isSelected
                  ? 'bg-system-blue text-white'
                  : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 text-gray-400 hover:text-white hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70'
              }`}
              style={isSelected ? {
                borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
                borderRight: '2px solid rgba(0, 0, 0, 0.4)',
                borderBottom: '2px solid rgba(0, 0, 0, 0.4)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              } : {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
              }}
            >
              {isSelected && (
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
              )}
              <span className="relative uppercase">{filter.replace('_', ' ')}</span>
            </button>
          );
        })}
      </div>

      {/* Shop Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item, index) => {
          const canAfford = profile.currency >= item.price;
          
          return (
            <div
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-4 sm:p-5 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all animate-fade-in cursor-pointer hover:scale-105"
              style={{ 
                animationDelay: `${index * 0.05}s`,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="text-center mb-3 sm:mb-4">
                {item.image ? (
                  <div className="flex justify-center mb-2">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-20 h-20 object-contain"
                      style={{
                        filter: 'drop-shadow(0 0 15px rgba(59, 130, 246, 0.7)) brightness(1.1)'
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-4xl sm:text-5xl mb-2">{item.icon}</div>
                )}
                <h3 className="font-orbitron font-bold text-base sm:text-lg text-white mb-2">{item.name}</h3>
                <div 
                  className="bg-gradient-to-b from-slate-900/60 via-slate-800/40 to-slate-900/60 backdrop-blur-sm rounded-2xl px-3 py-2 mx-2 border-t-2 border-l-2 border-slate-900/80 border-r-2 border-b-2 border-r-slate-600/40 border-b-slate-600/40"
                  style={{
                    boxShadow: 'inset 0 3px 8px rgba(0, 0, 0, 0.6), inset 0 1px 2px rgba(0, 0, 0, 0.4), inset 0 -1px 2px rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <p className="text-xs sm:text-sm text-gray-200 font-rajdhani line-clamp-2">{item.description}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <img 
                    src="/icons/icon-gold.png" 
                    alt="Gold"
                    className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                    style={{ filter: 'drop-shadow(0 0 6px rgba(252, 211, 77, 0.8))' }}
                  />
                  <span className="text-xl sm:text-2xl font-bold font-orbitron" style={{ color: '#FCD34D' }}>
                    {item.price}
                  </span>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    purchaseItem(item.id);
                  }}
                  disabled={!canAfford}
                  className={`px-4 py-2 rounded-2xl font-orbitron font-bold text-xs sm:text-sm tracking-wider transition-all relative overflow-hidden ${
                    canAfford
                      ? 'bg-system-blue text-white hover:scale-105 active:scale-95'
                      : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 text-gray-500 cursor-not-allowed border-t-2 border-l-2 border-slate-800/70 border-r-2 border-b-2 border-r-slate-900/80 border-b-slate-950/80'
                  }`}
                  style={canAfford ? {
                    borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                    borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRight: '2px solid rgba(0, 0, 0, 0.4)',
                    borderBottom: '2px solid rgba(0, 0, 0, 0.4)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.4)',
                    filter: 'brightness(1.1) saturate(1.2)'
                  } : {
                    boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.6), inset 0 1px 2px rgba(0, 0, 0, 0.4), inset 0 -1px 1px rgba(255, 255, 255, 0.03)'
                  }}
                >
                  {canAfford && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                  )}
                  <span className="relative">{canAfford ? 'BUY' : 'LOCKED'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Item Detail Modal */}
      <ShopItemDetailModal
        item={selectedItem}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedItem(null);
        }}
        onPurchase={handlePurchase}
        canAfford={selectedItem ? profile.currency >= selectedItem.price : false}
      />
    </div>
  );
};

export default ShopPage;



